import Grid2 from '@mui/material/Unstable_Grid2';
import { useState } from 'react';

import HighlightLightboxImagePlayer from '@/components/HighlightLightboxPlayer/HighlightLightboxImagePlayer';
import GridContainer2 from '@/components/Layout/GridContainer2/GridContainer2';
import RichText from '@/components/RichText/RichText';
import { FrontifyImageContent } from '@/contentful';
import { useInteraction } from '@/hooks/useInteraction';
import useViewPortSize from '@/hooks/useViewportSize';
import useGlobalStyles from '@/styles/global.styles';
import { EmbeddedImageCf } from '@/types';
import { setImgWidth, setSrcSetImg } from '@/utils/frontify';

import useStyles from './EmbeddedImage.styles';

export default function EmbeddedImage({
  content,
}: {
  content: EmbeddedImageCf;
}) {
  const { classes, cx } = useStyles();
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isMobile } = useViewPortSize();
  const { captionText, image, altText, modal } = content.fields;
  const [defaultImage, mobileImage] =
    image as unknown as FrontifyImageContent[];
  const lightBoxImage = mobileImage && isMobile ? mobileImage : defaultImage;
  const imageSize = isMobile ? 768 : 1440;

  const handleInteraction = useInteraction(() => {
    if (modal) setIsModalOpen(true);
  });

  if (!defaultImage) return null;

  return (
    <div className={classes.container}>
      <GridContainer2 fixed gridProps={{ className: classes.grid }}>
        <div className={classes.imageWrapper}>
          <div
            role='button'
            tabIndex={0}
            onClick={handleInteraction}
            onKeyDown={handleInteraction} // Unified handling
            aria-label={altText || 'Embedded Image'}
          >
            <img
              src={setImgWidth(defaultImage.preview_url, imageSize)}
              srcSet={setSrcSetImg(defaultImage.preview_url, imageSize)}
              alt={altText}
              data-testid='default-image'
              className={cx(classes.image, {
                [classes.hiddenOnMobile]: !!mobileImage,
              })}
            />
          </div>
          {mobileImage && (
            <div
              role='button'
              tabIndex={0}
              onClick={handleInteraction}
              onKeyDown={handleInteraction} // Unified handling
              aria-label={altText || 'Mobile Embedded Image'}
            >
              <img
                src={setImgWidth(mobileImage.preview_url, imageSize)}
                srcSet={setSrcSetImg(mobileImage.preview_url, imageSize)}
                alt={altText}
                data-testid='mobile-image'
                className={cx(classes.image, classes.mobileImage)}
              />
            </div>
          )}
          {captionText && (
            <Grid2>
              <RichText
                content={captionText}
                className={cx(globalClasses.caption, classes.caption)}
              />
            </Grid2>
          )}
        </div>
        <HighlightLightboxImagePlayer
          image={lightBoxImage}
          altText={altText || 'Enlarged Embedded Image'}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      </GridContainer2>
    </div>
  );
}
